import { useEffect, useRef } from 'react';
import { useAppDispatch } from '../store/hooks';
import { setPartnerOnline } from '../store/slices/coupleSlice';
import type { CoupleNudgeEvent } from '../types/couple';
import type { FantasyAnswersUpdatedEvent } from '../types/fantasyMatch';
import { loadEcho } from './echo';

interface PresenceMember {
  id: number;
  name?: string;
}

interface UseCoupleChannelOptions {
  coupleId: number | null;
  partnerId: number | null;
  /** A partner nudge arrived (never one of our own). */
  onNudge?: (event: CoupleNudgeEvent) => void;
  /**
   * The partner answered a fantasy (`.FantasyAnswersUpdated`: counts only,
   * never the answers; our own echoes are filtered out).
   */
  onFantasyAnswers?: (event: FantasyAnswersUpdatedEvent) => void;
}

/**
 * Joins `presence-couple.{id}`: members -> `partnerOnline`, `.CoupleNudge` ->
 * `onNudge`, `.FantasyAnswersUpdated` -> `onFantasyAnswers`. Without a websocket the flag stays false and the UI falls back to
 * the partner's `last_seen_at`.
 */
export function useCoupleChannel({
  coupleId,
  partnerId,
  onNudge,
  onFantasyAnswers,
}: UseCoupleChannelOptions): void {
  const dispatch = useAppDispatch();
  const onNudgeRef = useRef(onNudge);
  onNudgeRef.current = onNudge;
  const onFantasyAnswersRef = useRef(onFantasyAnswers);
  onFantasyAnswersRef.current = onFantasyAnswers;

  useEffect(() => {
    if (coupleId == null || partnerId == null) return undefined;

    let cancelled = false;
    let teardown: () => void = () => undefined;

    void loadEcho().then(echo => {
      if (cancelled || !echo) return;

      const channelName = `couple.${coupleId}`;
      const members = new Set<number>();
      const publish = () => {
        if (!cancelled) dispatch(setPartnerOnline(members.has(partnerId)));
      };

      echo
        .join(channelName)
        .here((users: PresenceMember[]) => {
          members.clear();
          users.forEach(user => members.add(user.id));
          publish();
        })
        .joining((user: PresenceMember) => {
          members.add(user.id);
          publish();
        })
        .leaving((user: PresenceMember) => {
          members.delete(user.id);
          publish();
        })
        .listen('.CoupleNudge', (event: CoupleNudgeEvent) => {
          if (event?.from_user_id === partnerId) onNudgeRef.current?.(event);
        })
        .listen(
          '.FantasyAnswersUpdated',
          (event: FantasyAnswersUpdatedEvent) => {
            if (event?.user_id === partnerId) {
              onFantasyAnswersRef.current?.(event);
            }
          }
        );

      teardown = () => echo.leave(channelName);
      if (cancelled) teardown();
    });

    return () => {
      cancelled = true;
      teardown();
      dispatch(setPartnerOnline(false));
    };
  }, [coupleId, dispatch, partnerId]);
}
